import * as yup from "yup";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";
import { useState, useEffect } from "react";

import Datepicker from "../../../components/common/Datepicker";
import FileUpload from "../../../components/common/FileUpload";
import Toaster from "../../../components/common/Toaster";

import complianceService from "../../../services/complianceService";
import moment from "moment";

import {
  MULTIMEDIA_PLACEHOLDER,
  MULTIMEDIA_MIME_TYPES,
  FILE_ALREADY_EXISTS,
} from "../../../utils/constants";

const SwornDeclarationForm = (props) => {
  const { data, account, isLoading, setLoading, setShowForm, onSuccess } =
    props;

  const [fileExists, setFileExists] = useState(false);
  const [year, setYear] = useState(null);

  const [currentYear] = useState(
    Date.parse(moment(new Date(), "YYYY").toISOString())
  );

  const schema = yup.object({
    year: yup.string().required("Please select year"),
    files: yup
      .mixed()
      .test(
        "is-valid-file-name",
        "A string in a file name must match with the first name.",
        (value) => isValidFileName(value)
      ),
  });

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const isValidFileName = (file) => {
    if (file === undefined) return true;

    let first_name = account.first_name;
    let file_name = file[0].name;

    first_name = first_name.toLowerCase();
    file_name = file_name.toLowerCase();

    return file_name.includes(first_name);
  };

  const onSubmit = async (info) => {
    try {
      setLoading(true);

      const next_deadline = moment([moment().year(), 0, 15]).add(1, "year");

      const sworn = { ...info };
      sworn["account_id"] = account.id;
      sworn["year"] = info.year.slice(info.year.length - 4);
      sworn["deadline"] = next_deadline.format("MM-DD-yyyy");

      const result = await complianceService.saveSwornDeclarationAsync(sworn);

      sworn["id"] = result.id;
      sworn["deadline"] = next_deadline.format("LL");
      sworn["created_on"] = moment().format("LL");

      if (result.s3_reference_names && result.s3_reference_names.length > 0) {
        sworn["s3_reference_name"] = result.s3_reference_names[0];
        sworn["document_name"] = result.files[0].file_name;
      }

      Toaster.Success("Sworn Declaration Saved Successfully.");
      onSuccess(sworn);
      setShowForm(false);

      setLoading(false);
    } catch (ex) {
      if (ex.response && ex.response.data.message === FILE_ALREADY_EXISTS) {
        setFileExists(true);
        setLoading(false);
        return;
      }

      Toaster.Error("Something went wrong.");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data) {
      const year = moment(data.year).format("yyyy");

      setValue("id", data.id);
      setValue("year", year);
      setYear(year);
    }
  }, [data, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <button className="overflow-hidden h-0 w-0" />
          <div className="-mt-4">
            <Datepicker
              id="year-datepicker"
              name="year"
              title="Year"
              dateFormat="YYYY"
              setValue={setValue}
              clearErrors={clearErrors}
              register={register("year")}
              timeFormat={false}
              initialValue={currentYear}
              onDateChange={(date) => setYear(date)}
              value={year}
              errors={errors}
              readOnly
            />
          </div>
        </div>
      </div>
      {!data && (
        <div className="grid grid-cols-2">
          <div className="col-span-2">
            <FileUpload
              id="files-dropdown"
              name="files"
              title="Files"
              setValue={setValue}
              clearErrors={clearErrors}
              register={register("files")}
              errors={errors}
              accepts={MULTIMEDIA_MIME_TYPES}
              placeholder={MULTIMEDIA_PLACEHOLDER}
              maxFiles={1}
              maxSize={5242880}
              multiple={false}
            />
            {fileExists && (
              <div className="pt-2">
                <span className="text-sm text-red-400">
                  {FILE_ALREADY_EXISTS}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default SwornDeclarationForm;
